import {isFinite} from 'lodash';
import {deepClone} from '@packages/systems/core/utils/JsUtils';
import {cssPropDetails, cssPropFromPath} from './CssPropertyUtils';
import $ from 'jquery';

import type {WebflowCssProperty} from '@packages/systems/core/utils/WebflowCssSubset';
import type {Unit, UnitConversions} from '@packages/systems/style-legacy/utils';

export const UNIT_DETAILS_MAP = {
  '-': {unit: '-', empty: true, precise: true, label: '-'},
  auto: {unit: 'auto', empty: false, precise: false, label: ''},
  px: {unit: 'px', empty: false, precise: false, label: 'Pixels'},
  fr: {unit: 'fr', empty: false, precise: false, label: 'Fractional'},
  '%': {unit: '%', empty: false, precise: false, label: 'Percent'},
  em: {unit: 'em', empty: false, precise: true, label: 'Elastic measures'},
  rem: {
    unit: 'rem',
    empty: false,
    precise: true,
    label: 'Root elastic measures',
  },
  ch: {unit: 'ch', empty: false, precise: true, label: 'Characters'},
  deg: {unit: 'deg', empty: false, precise: false, label: 'Degrees'},
  grad: {unit: 'grad', empty: false, precise: false, label: 'Gradians'},
  rad: {unit: 'rad', empty: false, precise: false, label: 'Radians'},
  turn: {unit: 'turn', empty: false, precise: false, label: 'Turn'},
  pt: {unit: 'pt', empty: false, precise: false, label: 'Points'},
  cm: {unit: 'cm', empty: false, precise: false, label: 'Centimeters'},
  mm: {unit: 'mm', empty: false, precise: false, label: 'Millimeters'},
  s: {unit: 's', empty: false, precise: false, label: 'Seconds'},
  ms: {unit: 'ms', empty: false, precise: false, label: 'Milliseconds'},
  vh: {unit: 'vh', empty: false, precise: false, label: 'Vertical heights'},
  vw: {unit: 'vw', empty: false, precise: false, label: 'Vertical widths'},
  dvh: {
    unit: 'dvh',
    empty: false,
    precise: false,
    label: 'Dynamic vertical heights',
  },
  dvw: {
    unit: 'dvw',
    empty: false,
    precise: false,
    label: 'Dynamic vertical widths',
  },
  svh: {
    unit: 'svh',
    empty: false,
    precise: false,
    label: 'Small vertical heights',
  },
  svw: {
    unit: 'svw',
    empty: false,
    precise: false,
    label: 'Small vertical widths',
  },
  lvh: {
    unit: 'lvh',
    empty: false,
    precise: false,
    label: 'Large vertical heights',
  },
  lvw: {
    unit: 'lvw',
    empty: false,
    precise: false,
    label: 'Large vertical widths',
  },
  vmin: {
    unit: 'vmin',
    empty: false,
    precise: false,
    label: 'Vertical minimums',
  },
  vmax: {
    unit: 'vmax',
    empty: false,
    precise: false,
    label: 'Vertical maximums',
  },
} as const;

export const DEFAULT_UNIT_CONVERSIONS: UnitConversions = {
  // @ts-expect-error - TS2322 - Type '{ px: { '%': (pxValue: any, propName: any, nativeSelectedNode: any) => number; }; em: { '%': (emValue: any, propName: any, nativeSelectedNode: any) => number; }; '%': { px: (oldValue: any, propName: any, nativeSelectedNode: any) => number; em: (oldValue: any, propName: any, nativeSelectedNode: any) => number; }; nul...' is not assignable to type 'UnitConversions'.
  px: {
    // @ts-expect-error - TS7006 - Parameter 'pxValue' implicitly has an 'any' type. | TS7006 - Parameter 'propName' implicitly has an 'any' type. | TS7006 - Parameter 'nativeSelectedNode' implicitly has an 'any' type.
    '%': function (pxValue, propName, nativeSelectedNode): number {
      const selectedEl = $(nativeSelectedNode);

      if (!pxValue) {
        pxValue = selectedEl.outerWidth();
      }

      const parent = selectedEl.parent();
      const paddingLeft = parseInt(parent.css('padding-left'), 10);
      const paddingRight = parseInt(parent.css('padding-right'), 10);
      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
      const total = parent.outerWidth() - paddingLeft - paddingRight;
      const oldPrecision = getNumberPrecision(pxValue);
      const percent = preciseNumber((pxValue / total) * 100, oldPrecision);

      return percent;
    },
  },
  em: {
    // @ts-expect-error - TS7006 - Parameter 'emValue' implicitly has an 'any' type. | TS7006 - Parameter 'propName' implicitly has an 'any' type. | TS7006 - Parameter 'nativeSelectedNode' implicitly has an 'any' type.
    '%': function (emValue, propName, nativeSelectedNode): number {
      const selectedEl = $(nativeSelectedNode);

      if (!emValue) {
        emValue = selectedEl.outerWidth();
      }

      const parent = selectedEl.parent();
      const paddingLeft = parseInt(parent.css('padding-left'), 10);
      const paddingRight = parseInt(parent.css('padding-right'), 10);
      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
      const total = parent.outerWidth() - paddingLeft - paddingRight;
      const oldPrecision = getNumberPrecision(emValue);
      const percent = preciseNumber((emValue / total) * 100, oldPrecision);

      return percent;
    },
  },
  '%': {
    // @ts-expect-error - TS7006 - Parameter 'oldValue' implicitly has an 'any' type. | TS7006 - Parameter 'propName' implicitly has an 'any' type. | TS7006 - Parameter 'nativeSelectedNode' implicitly has an 'any' type.
    px: function (oldValue, propName, nativeSelectedNode): number {
      const oldPrecision = getNumberPrecision(oldValue);
      return preciseNumber(
        propValue(propName, nativeSelectedNode),
        oldPrecision
      );
    },
    // @ts-expect-error - TS7006 - Parameter 'oldValue' implicitly has an 'any' type. | TS7006 - Parameter 'propName' implicitly has an 'any' type. | TS7006 - Parameter 'nativeSelectedNode' implicitly has an 'any' type.
    em: function (oldValue, propName, nativeSelectedNode): number {
      const oldPrecision = getNumberPrecision(oldValue);
      return preciseNumber(
        propValue(propName, nativeSelectedNode),
        oldPrecision
      );
    },
  },
  null: {
    '%': 100,
    em: function (oldValue, propName, nativeSelectedNode): number {
      const oldPrecision = getNumberPrecision(oldValue);
      return preciseNumber(
        propValue(propName, nativeSelectedNode),
        oldPrecision
      );
    },
    px: function (oldValue, propName, nativeSelectedNode): number {
      const oldPrecision = getNumberPrecision(oldValue);
      return preciseNumber(
        propValue(propName, nativeSelectedNode),
        oldPrecision
      );
    },
  },
};

export const WIDTH_UNIT_CONVERSIONS = DEFAULT_UNIT_CONVERSIONS;

export const HEIGHT_UNIT_CONVERSIONS = (() => {
  const result = deepClone(DEFAULT_UNIT_CONVERSIONS);

  // @ts-expect-error - TS2339 - Property 'px' does not exist on type 'UnitConversions'. | TS2339 - Property 'em' does not exist on type 'UnitConversions'.
  result.px['%'] = result.em['%'] = (
    // @ts-expect-error - TS7006 - Parameter 'oldValue' implicitly has an 'any' type.
    oldValue,
    propName: WebflowCssProperty,
    nativeSelectedNode: Node
  ): number => {
    const selectedEl = $(nativeSelectedNode);

    if (!oldValue) {
      oldValue = selectedEl.outerHeight();
    }

    const parent = selectedEl.parent();
    const paddingTop = parseInt(parent.css('padding-top'), 10);
    const paddingBottom = parseInt(parent.css('padding-bottom'), 10);
    // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
    const total = parent.outerHeight() - paddingTop - paddingBottom;
    const oldPrecision = getNumberPrecision(oldValue);
    const percent = preciseNumber((oldValue / total) * 100, oldPrecision);

    return percent;
  };

  return result;
})();

export const FONT_SIZE_UNIT_CONVERSIONS = (function () {
  const result = deepClone(DEFAULT_UNIT_CONVERSIONS);

  const units = ['px', 'em', '%'];
  const unitFactors = {
    px: 16,
    em: 1,
    '%': 100,
    pt: 12,
  } as const;

  // @ts-expect-error - TS2339 - Property 'px' does not exist on type 'UnitConversions'.
  result.px.em = () => null;

  units.forEach(function (oldUnit) {
    units.forEach(function (newUnit) {
      if (oldUnit !== newUnit) {
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'UnitConversions'.
        result[oldUnit][newUnit] = function (
          oldValue: null | number,
          propName: WebflowCssProperty,
          nativeSelectedNode: Node
        ): number | null {
          if (oldValue === null) {
            return null;
          }
          const oldPrecision = getNumberPrecision(oldValue);
          return preciseNumber(
            Number(
              // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly px: 16; readonly em: 1; readonly '%': 100; readonly pt: 12; }'. | TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly px: 16; readonly em: 1; readonly '%': 100; readonly pt: 12; }'.
              (((oldValue / unitFactors[oldUnit]) * unitFactors[newUnit]) /
                (oldUnit === 'px'
                  ? bodyFontSizeFactor(nativeSelectedNode)
                  : 1)) *
                (newUnit === 'px' ? bodyFontSizeFactor(nativeSelectedNode) : 1)
            ),
            oldPrecision
          );
        };
      }
    });
  });

  return result;
})();

/**
 * Looks up the unit descriptor for a given unit name.
 * @param  {String} unitName  The unit name to look up, e.g. "px"
 * @return {Object}           The unit descriptor.
 */
export const unitDetails = (unitName: Unit) => UNIT_DETAILS_MAP[unitName];

/**
 * Looks up the available units for a specific CSS property.
 * @param  {String} cssProp The name of the CSS property, e.g. 'margin-top'
 * @return {Object}         The unit details of the property.
 */
const unitDetailsFromProp = (cssProp: WebflowCssProperty) =>
  (cssPropDetails(cssProp)?.units || []).map(unitDetails);

/**
 * Looks up unit details for a model path.
 * @param  {String} path The dot-separated model path, e.g. 'margin.top'
 * @return {Object}      Unit details for the CSS property that matches this model path.
 */
export const unitDetailsFromPath = (path: string) => {
  const cssProp = cssPropFromPath(path);
  if (cssProp == null) return null;
  return unitDetailsFromProp(
    // @ts-expect-error - cssProp is plain string but `unitDetailsFromProp` expects `WebflowCssProperty`
    cssProp
  );
};

function bodyFontSizeFactor(nativeSelectedNode: Node) {
  // @ts-expect-error - TS18047 - 'nativeSelectedNode.ownerDocument' is possibly 'null'.
  const view = nativeSelectedNode.ownerDocument.defaultView;
  return (
    parseFloat(
      // @ts-expect-error - TS18047 - 'view' is possibly 'null'. | TS2531 - Object is possibly 'null'.
      view
        // @ts-expect-error - TS2345 - Argument of type 'Node' is not assignable to parameter of type 'Element'.
        .getComputedStyle(nativeSelectedNode)
        .getPropertyValue('font-size')
        .match(/^([0-9.]+)px$/)[1]
    ) / 16
  );
}

function propValue(propName: WebflowCssProperty, nativeSelectedNode: Node) {
  const selectedEl = $(nativeSelectedNode);
  if (/^(min-|max-)?width$/.test(propName)) {
    return selectedEl.outerWidth();
  } else if (/^(min-|max-)?height$/.test(propName)) {
    return selectedEl.outerHeight();
  } else {
    const valueFromDom = parseInt(selectedEl.css(propName), 10);
    return isFinite(valueFromDom) ? valueFromDom : 0;
  }
}

// @ts-expect-error - TS7006 - Parameter 'n' implicitly has an 'any' type.
function getNumberPrecision(n) {
  const match = String(n).match(/\.([0-9]+)$/);

  if (match) {
    // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
    return match[1].length;
  } else {
    return 0;
  }
}

// @ts-expect-error - TS7006 - Parameter 'n' implicitly has an 'any' type.
function preciseNumber(n, precision: number) {
  return Number(Number(n).toFixed(precision));
}
